/**
 * SPRINT V2 — Recompensas Sociales Nacionales.
 * Daily/weekly social rewards earned by defending the nation (share, hype,
 * predict). Local progress, deterministic catalogue.
 */
const K = "vs:v2:social-rewards:v1";

export type NationalRewardKind = "share" | "hype" | "predict" | "defend";

export interface NationalRewardDef {
  id: string;
  kind: NationalRewardKind;
  title: string;
  emoji: string;
  goal: number;
  reward: number;
}

export interface NationalRewardProgress {
  id: string;
  progress: number;
  claimed: boolean;
}

export const NATIONAL_REWARDS: NationalRewardDef[] = [
  { id: "share_flag",     kind: "share",   title: "Iza la bandera",       emoji: "🚩", goal: 3, reward: 25 },
  { id: "hype_wave",      kind: "hype",    title: "Ola de hype nacional", emoji: "🌊", goal: 10, reward: 30 },
  { id: "predict_loyal",  kind: "predict", title: "Predicción leal",      emoji: "🔮", goal: 5, reward: 40 },
  { id: "defend_pride",   kind: "defend",  title: "Defiende el orgullo",  emoji: "🛡️", goal: 2, reward: 60 },
];

interface StoreShape { day: string; rows: NationalRewardProgress[] }

function today() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
}

function read(): StoreShape {
  if (typeof window === "undefined") return { day: today(), rows: [] };
  try {
    const raw = window.localStorage.getItem(K);
    if (!raw) return { day: today(), rows: [] };
    const parsed = JSON.parse(raw) as StoreShape;
    if (parsed.day !== today()) return { day: today(), rows: [] };
    return parsed;
  } catch { return { day: today(), rows: [] }; }
}

function write(s: StoreShape) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(K, JSON.stringify(s)); } catch { /* noop */ }
}

export function listNationalRewards(): Array<NationalRewardDef & NationalRewardProgress> {
  const s = read();
  const map = new Map(s.rows.map((r) => [r.id, r]));
  return NATIONAL_REWARDS.map((d) => {
    const p = map.get(d.id) ?? { id: d.id, progress: 0, claimed: false };
    return { ...d, ...p };
  });
}

export function bumpNationalReward(kind: NationalRewardKind, delta = 1) {
  const s = read();
  const next: StoreShape = { day: today(), rows: NATIONAL_REWARDS.map((d) => {
    const cur = s.rows.find((r) => r.id === d.id) ?? { id: d.id, progress: 0, claimed: false };
    if (d.kind !== kind || cur.claimed) return cur;
    return { ...cur, progress: Math.min(d.goal, cur.progress + delta) };
  }) };
  write(next);
  return next.rows;
}

export function claimNationalReward(id: string): { ok: boolean; reward: number } {
  const def = NATIONAL_REWARDS.find((d) => d.id === id);
  if (!def) return { ok: false, reward: 0 };
  const s = read();
  const cur = s.rows.find((r) => r.id === id) ?? { id, progress: 0, claimed: false };
  if (cur.claimed || cur.progress < def.goal) return { ok: false, reward: 0 };
  const next: StoreShape = {
    day: today(),
    rows: [...s.rows.filter((r) => r.id !== id), { ...cur, claimed: true }],
  };
  write(next);
  return { ok: true, reward: def.reward };
}